import { SYSTEM_LAYER_NAME_SET } from '@designdeck/shared';
import type { DirectSaleCommitPosition } from '@/api/directSale';
import { positionToMm } from '@/lib/positionMm';
import type { PositionState } from '@/stores/canvasStateManager';

/** Whether a serialized canvas object is an editor layer, not artwork. */
function isSystemObject(object: unknown): boolean {
  if (!object || typeof object !== 'object') return false;
  const name = (object as { name?: unknown }).name;
  return typeof name === 'string' && SYSTEM_LAYER_NAME_SET.has(name);
}

/**
 * The positions a Direct Sale commit sends, from what the editor holds per
 * position. System layers (print area frame, guides) are dropped from the
 * canvas and a position left without artwork is not sent; the artwork's
 * own fields — an image's assetId above all — go through untouched. Null
 * while a decorated position has no canvas or no placement yet, or nothing
 * is left to send:
 * the commit waits rather than freezing an incomplete design.
 */
export function buildCommitPositions(
  decoratedPositions: ReadonlyArray<{ positionId: string }>,
  productPositions: ReadonlyArray<{ id: string; slug: string }>,
  masterPositions: Partial<Record<string, PositionState>>
): DirectSaleCommitPosition[] | null {
  const positions: DirectSaleCommitPosition[] = [];
  for (const decorated of decoratedPositions) {
    const position = productPositions.find(
      (candidate) => candidate.id === decorated.positionId
    );
    const state = position ? masterPositions[position.slug] : undefined;
    if (!position || !state?.json) return null;
    const json = state.json as unknown as Record<string, unknown>;
    const objects = Array.isArray(json.objects)
      ? json.objects.filter((object) => !isSystemObject(object))
      : [];
    if (objects.length === 0) continue;
    // Stored like every design position: millimetre JSON on the area of the
    // template it was edited on, placement from its bounding box (R34, R35).
    const { canvasData, placementMm } = positionToMm(
      { ...json, objects: objects as Array<Record<string, unknown>> },
      state.printContext
    );
    if (!placementMm) return null;
    positions.push({
      positionSlug: position.slug,
      canvasData,
      placementMm,
      designCropUrl: state.designCropUrl,
    });
  }
  return positions.length > 0 ? positions : null;
}

/**
 * The commit key: a hash of the shop's correlation id and the whole
 * configuration, quantities included. The same configuration commits to
 * the same design; any change — another quantity too — makes a new one.
 */
export async function commitKeyFor(parts: {
  sessionRef: string | null;
  productId: string;
  colorId: string | null;
  sizeId: string | null;
  items: ReadonlyArray<{ sizeId: string; quantity: number }>;
  positions: DirectSaleCommitPosition[];
  /** The picked decoration method; absent when each default applies. */
  decorationMethodId?: string;
}): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(parts));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
